// @ts-check

/** @file Applies authoritative TaskBoard command responses through the owning SyncClient. */

import {forcedBoolean, forcedInteger, forcedString, optionalString} from "typanic"
import SyncApiClient from "velocious/build/src/sync/sync-api-client.js"

/** @import {TaskBoardPlaceOnBoardResponse, TaskBoardRemoveFromBoardResponse} from "../../../shared/task-board-placement-command-types.js" */
/** @import {OpenProjectReplica} from "./project-replica.js" */

/**
 * Applies a successful online placement response to the same immutable replica
 * observed by mounted live queries.
 * @param {{projectId: string, replica: OpenProjectReplica, response: TaskBoardPlaceOnBoardResponse}} args Command result and captured ownership.
 * @returns {Promise<void>} Completion after the authoritative row is durable.
 * @rejects {Error} When the response is malformed, misrouted, or cannot be applied.
 */
export async function reconcileTaskBoardPlaceResponse({projectId, replica, response}) {
  const taskBoardId = forcedString(response.taskBoardId, "TaskBoard placement response taskBoardId")
  const boardProjectId = forcedString(response.boardProjectId, "TaskBoard placement response boardProjectId")
  const taskProjectId = forcedString(response.taskProjectId, "TaskBoard placement response taskProjectId")
  const replicaProjectIds = replicaProjects(response.replicaProjectIds)

  assertCapturedReplica({projectId, replica, replicaProjectIds})
  const expectedProjectIds = [...new Set([boardProjectId, taskProjectId])].sort()
  if (JSON.stringify(replicaProjectIds) !== JSON.stringify(expectedProjectIds)) {
    throw new Error("TaskBoard placement response has mismatched replica project routing")
  }
  forcedBoolean(response.created, "TaskBoard placement response created")
  const data = {
    boardColumnId: forcedString(response.boardColumnId, "TaskBoard placement response boardColumnId"),
    boardColumnName: optionalString(response.boardColumnName, "TaskBoard placement response boardColumnName"),
    boardId: forcedString(response.boardId, "TaskBoard placement response boardId"),
    boardName: optionalString(response.boardName, "TaskBoard placement response boardName"),
    boardProjectId,
    createdAt: forcedString(response.createdAt, "TaskBoard placement response createdAt"),
    id: taskBoardId,
    rowNumber: forcedInteger(response.rowNumber, "TaskBoard placement response rowNumber"),
    taskId: forcedString(response.taskId, "TaskBoard placement response taskId"),
    updatedAt: forcedString(response.updatedAt, "TaskBoard placement response updatedAt")
  }

  await replica.syncClient.remoteApplySync({source: "TaskBoard placement command response"})(SyncApiClient.syncEnvelopeFromPayload({
    data,
    id: `command-response:${taskBoardId}`,
    resourceId: taskBoardId,
    resourceType: "TaskBoard",
    syncType: "update"
  }))
}

/**
 * Applies a successful online removal response through the captured replica.
 * @param {{projectId: string, replica: OpenProjectReplica, response: TaskBoardRemoveFromBoardResponse}} args Command result and captured ownership.
 * @returns {Promise<void>} Completion after the authoritative deletion is durable.
 * @rejects {Error} When the response is malformed, misrouted, or cannot be applied.
 */
export async function reconcileTaskBoardRemoveResponse({projectId, replica, response}) {
  const replicaProjectIds = replicaProjects(response.replicaProjectIds)

  assertCapturedReplica({projectId, replica, replicaProjectIds})
  const removed = forcedBoolean(response.removed, "TaskBoard removal response removed")
  const taskBoardId = optionalString(response.taskBoardId, "TaskBoard removal response taskBoardId")

  forcedString(response.boardId, "TaskBoard removal response boardId")
  forcedString(response.taskId, "TaskBoard removal response taskId")
  if (!removed) {
    if (taskBoardId !== null) throw new Error("TaskBoard removal response returned an id without removing a placement")
    return
  }
  if (!taskBoardId) throw new Error("TaskBoard removal response omitted the removed placement id")

  await replica.syncClient.remoteApplySync({source: "TaskBoard removal command response"})(SyncApiClient.syncEnvelopeFromPayload({
    data: {id: taskBoardId},
    id: `command-response:${taskBoardId}`,
    resourceId: taskBoardId,
    resourceType: "TaskBoard",
    syncType: "delete"
  }))
}

/**
 * Validates the server-declared replica list without coercion or widening.
 * @param {unknown} value Raw response value.
 * @returns {string[]} Sorted unique project ids.
 * @throws {TypeError | Error} When the response list is missing, malformed, or duplicated.
 */
function replicaProjects(value) {
  if (!Array.isArray(value) || value.length === 0) {
    throw new TypeError("Expected TaskBoard command response replicaProjectIds to be a non-empty array")
  }
  const projectIds = value.map((projectId, index) => forcedString(projectId, `TaskBoard command response replicaProjectIds[${index}]`))
  const uniqueProjectIds = [...new Set(projectIds)].sort()

  if (uniqueProjectIds.length !== projectIds.length) {
    throw new Error("TaskBoard command response replicaProjectIds contains duplicates")
  }

  return uniqueProjectIds
}

/**
 * Fails closed unless the response belongs to this exact immutable handle.
 * @param {{projectId: string, replica: OpenProjectReplica, replicaProjectIds: string[]}} args Captured ownership.
 * @returns {void}
 * @throws {Error} When the captured handle or authorized routing does not match.
 */
function assertCapturedReplica({projectId, replica, replicaProjectIds}) {
  if (replica.identity.projectId !== projectId) {
    throw new Error(`Captured project replica ${replica.identity.projectId} does not own requested project ${projectId}`)
  }
  if (!replicaProjectIds.includes(projectId)) {
    throw new Error(`TaskBoard command response does not authorize project ${projectId}`)
  }
}
